import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Search, ExternalLink, CheckCircle2, AlertTriangle, XCircle, HelpCircle, ShieldAlert, Link2, FileText } from 'lucide-react'
import { useExternalLinkDomains, useExternalLinkDomainDetail, useExternalLinkStatus, useExternalSpamScores } from '../hooks/useCrawls'
import { cn } from '@/lib/utils'

function StatusBadge({ statusCode, isBroken }: { statusCode: number | null; isBroken: number }) {
  if (statusCode === null || statusCode === undefined) {
    return <Badge variant="outline" className="gap-1"><HelpCircle className="h-3 w-3" />Unchecked</Badge>
  }
  if (isBroken) {
    return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{statusCode || 'Error'}</Badge>
  }
  if (statusCode >= 300 && statusCode < 400) {
    return <Badge variant="secondary" className="gap-1"><AlertTriangle className="h-3 w-3" />{statusCode}</Badge>
  }
  return <Badge className="gap-1 bg-green-600 hover:bg-green-600"><CheckCircle2 className="h-3 w-3" />{statusCode}</Badge>
}

const STATUS_FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'ok', label: 'OK' },
  { key: 'redirect', label: 'Redirect' },
  { key: 'broken', label: 'Broken' },
  { key: 'unchecked', label: 'Unchecked' },
] as const

function SpamScoreBadge({ score }: { score: number }) {
  if (score >= 60) return <Badge variant="destructive" className="gap-1 text-xs"><ShieldAlert className="h-3 w-3" />{score}</Badge>
  if (score >= 30) return <Badge variant="secondary" className="gap-1 text-xs text-yellow-700 dark:text-yellow-400">{score}</Badge>
  return <span className="text-xs text-muted-foreground">{score}</span>
}

export function ExternalLinksTab({ crawlId, checkExternalLinks }: { crawlId: string; checkExternalLinks?: boolean }) {
  const { data: domainData, isLoading: domainsLoading } = useExternalLinkDomains(crawlId)
  const { data: spamData } = useExternalSpamScores(crawlId)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null)

  const { data: statusData, isLoading: statusLoading } = useExternalLinkStatus(crawlId, statusFilter)
  const { data: domainDetail, isLoading: detailLoading } = useExternalLinkDomainDetail(crawlId, selectedDomain)

  const spamMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const s of spamData?.scores || []) map.set(s.target, s.spam_score)
    return map
  }, [spamData])

  const domains = domainData?.domains || []
  const totalDomains = domainData?.totalCount ?? domains.length
  const filtered = search
    ? domains.filter((d) => d.domain.toLowerCase().includes(search.toLowerCase()))
    : domains

  const totalLinks = domains.reduce((sum, d) => sum + d.link_count, 0)
  const spammyCount = domains.filter((d) => (spamMap.get(d.domain) ?? 0) >= 60).length

  if (domainsLoading) return <Skeleton className="h-48 w-full" />

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex gap-4 text-sm flex-wrap">
        <div className="rounded-lg border px-4 py-2">
          <span className="text-muted-foreground">Domains: </span>
          <span className="font-semibold">
            {totalDomains > domains.length ? `${domains.length} of ${totalDomains}` : domains.length}
          </span>
        </div>
        <div className="rounded-lg border px-4 py-2">
          <span className="text-muted-foreground">Total links: </span>
          <span className="font-semibold">{totalLinks}</span>
        </div>
        {spammyCount > 0 && (
          <div className="rounded-lg border px-4 py-2 border-destructive/30">
            <span className="text-muted-foreground">Potentially Spammy: </span>
            <span className="font-semibold text-destructive">{spammyCount} domain{spammyCount !== 1 ? 's' : ''}</span>
          </div>
        )}
        {checkExternalLinks && statusData?.summary && (
          <>
            <div className="rounded-lg border px-4 py-2 border-green-600/30">
              <span className="text-muted-foreground">OK: </span>
              <span className="font-semibold text-green-600 dark:text-green-400">{statusData.summary.ok}</span>
            </div>
            <div className="rounded-lg border px-4 py-2 border-yellow-600/30">
              <span className="text-muted-foreground">Redirect: </span>
              <span className="font-semibold text-yellow-600 dark:text-yellow-400">{statusData.summary.redirect}</span>
            </div>
            <div className="rounded-lg border px-4 py-2 border-destructive/30">
              <span className="text-muted-foreground">Broken: </span>
              <span className="font-semibold text-destructive">{statusData.summary.broken}</span>
            </div>
            {statusData.summary.unchecked > 0 && (
              <div className="rounded-lg border px-4 py-2">
                <span className="text-muted-foreground">Unchecked: </span>
                <span className="font-semibold">{statusData.summary.unchecked}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Status filter pills (only when external checking was enabled) */}
      {checkExternalLinks && (
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key ?? 'all'}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                statusFilter === f.key
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50'
              )}
            >
              {f.label}
              {f.key && statusData?.summary && (
                <span className="ml-1 text-muted-foreground">
                  ({statusData.summary[f.key as keyof typeof statusData.summary]})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Status view — individual URLs with status codes */}
      {checkExternalLinks && statusFilter !== undefined && (
        <div className="rounded-md border overflow-x-auto">
          {statusLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[300px]">URL</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[80px] text-right">Links</TableHead>
                  <TableHead className="w-[100px] text-right">Sources</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(statusData?.links || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No external links match this filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  (statusData?.links || []).map((link) => (
                    <TableRow key={link.target_url}>
                      <TableCell className="font-mono text-xs min-w-0">
                        <a
                          href={link.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 hover:underline truncate max-w-[500px]"
                          title={link.target_url}
                        >
                          {link.target_url}
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                        {link.redirect_url && (
                          <div className="mt-0.5 text-[10px] text-muted-foreground truncate max-w-[500px]" title={link.redirect_url}>
                            → {link.redirect_url}
                            {link.final_status_code && link.final_status_code !== link.status_code && ` (${link.final_status_code})`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge statusCode={link.status_code} isBroken={link.is_broken} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{link.link_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {link.source_pages}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Domain view — default when no status filter or no external checking */}
      {(!checkExternalLinks || statusFilter === undefined) && (
        <>
          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter domains..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {filtered.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {search ? 'No domains match your search.' : 'No external links found.'}
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px]">Domain</TableHead>
                    <TableHead className="w-[100px] text-right">Status</TableHead>
                    <TableHead className="w-[100px] text-right">Links</TableHead>
                    <TableHead className="w-[120px] text-right">Source Pages</TableHead>
                    {spamMap.size > 0 && <TableHead className="w-[100px] text-right">Spam Score</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d) => (
                    <TableRow key={d.domain} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedDomain(d.domain)}>
                      <TableCell className="font-mono text-xs">
                        <span className="inline-flex items-center gap-1.5 hover:underline text-primary">
                          {d.domain}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {d.worst_status == null ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <Badge variant={
                            d.worst_status >= 500 ? 'destructive'
                            : d.worst_status >= 400 ? 'destructive'
                            : d.worst_status >= 300 ? 'secondary'
                            : 'outline'
                          }>{d.worst_status}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{d.link_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {d.source_pages}
                      </TableCell>
                      {spamMap.size > 0 && (
                        <TableCell className="text-right">
                          {spamMap.has(d.domain) ? <SpamScoreBadge score={spamMap.get(d.domain)!} /> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
      {/* Domain detail sheet */}
      <Sheet open={!!selectedDomain} onOpenChange={(open) => !open && setSelectedDomain(null)}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-mono text-sm break-all pr-8">
              <a href={`https://${selectedDomain}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:underline">
                {selectedDomain}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </SheetTitle>
            <SheetDescription>
              Source pages and anchor text for links to this domain
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {detailLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : !domainDetail?.links?.length ? (
              <p className="text-sm text-muted-foreground py-4">No links found.</p>
            ) : (
              <>
                <div className="text-sm text-muted-foreground">
                  {domainDetail.links.length} link{domainDetail.links.length !== 1 ? 's' : ''} from{' '}
                  {new Set(domainDetail.links.map((l) => l.source_url)).size} source page{new Set(domainDetail.links.map((l) => l.source_url)).size !== 1 ? 's' : ''}
                </div>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">
                          <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" />Source Page</span>
                        </TableHead>
                        <TableHead className="min-w-[200px]">
                          <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />Target URL</span>
                        </TableHead>
                        <TableHead className="min-w-[120px]">Anchor Text</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {domainDetail.links.map((link, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">
                            <a
                              href={link.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline text-primary truncate block max-w-[300px]"
                              title={link.source_url}
                            >
                              {new URL(link.source_url).pathname}
                            </a>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <a
                              href={link.target_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline truncate block max-w-[300px]"
                              title={link.target_url}
                            >
                              {link.target_url.replace(/^https?:\/\//, '')}
                              <ExternalLink className="h-3 w-3 ml-1 inline shrink-0 text-muted-foreground" />
                            </a>
                          </TableCell>
                          <TableCell className="text-xs">
                            {link.anchor_text ? (
                              <span>{link.anchor_text}</span>
                            ) : (
                              <span className="text-muted-foreground italic">empty</span>
                            )}
                            {link.is_nofollow ? (
                              <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0">nofollow</Badge>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
